/**
 * The funnel editor — six tabs over one draft.
 *
 * Visual Reference: the chrome shared across `design/screens/editor-*.html`.
 * Each tab's BODY belongs to its own story (6.3 Build, 6.4 Logic, 7.1 Qualify,
 * 6.5 Design, 9.1 Connect, 8.7 Results); this is the shell they land in.
 *
 * THERE IS NO SAVE BUTTON, in any of the six editor exports, in any state. That
 * is a design decision, not an omission — so the draft saves itself. What the
 * exports also do not draw is a "Saved" indicator, which leaves a real gap:
 * silent success is fine, and silent FAILURE is the failure mode RULE-078 is
 * written about ("a failure whose resolution needs a human awake"). So a save
 * that fails says so, in the `Callout` the design system already specifies,
 * and keeps every edit in memory to retry. Parked as Q-043.
 *
 * Undo and redo have no drawn control either, so they are the keyboard's:
 * Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z, plus Ctrl+Y for the Windows habit. Same
 * question.
 *
 * Story 6.2.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Funnel } from '@leadgible/schema'
import { ApiFailure, apiGet, apiPost, apiPut } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Skeleton } from '../ui/Skeleton'
import { BuildTab } from '../editor/BuildTab'
import { DesignTab } from '../editor/DesignTab'
import { ConnectTab } from '../editor/ConnectTab'
import { EditorChrome } from '../editor/EditorChrome'
import { PublishDialog } from '../editor/PublishDialog'
import { outcomeOf, type Outcome } from '../editor/publish-outcome'
import { QualifyTab } from '../editor/QualifyTab'
import { ResultsTab } from '../editor/ResultsTab'
import { LogicTab } from '../editor/LogicTab'
import { ProposalView } from '../editor/ProposalView'
import { DRAWN as PROPOSAL } from '../editor/proposal-copy'
import { PROPOSING } from '../editor/proposal-request'
import { TABS, tabFromPath, type TabId } from '../editor/tabs'
import {
  edit,
  editorState,
  load,
  redo,
  reset,
  setSaveState,
  undo,
  useEditor,
} from '../editor/store'

type Draft = {
  id: string
  name: string
  archetype: string
  status: string
  schema: Funnel
  updatedAt: number
  currentVersionId: string | null
}

/** How long after the last keystroke the draft goes to the server. */
const AUTOSAVE_MS = 1200

/** `/funnels/{fid}` and `/funnels/{fid}/{tab}` — the app's first route params. */
export function editorPath(pathname: string): { funnelId: string; tab: TabId } | null {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean)
  if (parts[0] !== 'funnels' || !parts[1]) return null
  return { funnelId: parts[1], tab: tabFromPath(parts[2]) }
}

export function Editor() {
  const { workspace, me, org } = useShell()
  const route = editorPath(window.location.pathname)

  const funnel = useEditor((s) => s.funnel)
  const save = useEditor((s) => s.save)
  const [meta, setMeta] = useState<Pick<Draft, 'status' | 'archetype'> | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabId>(route?.tab ?? 'build')

  /** Story 16.1. Null means the confirmation is closed. */
  const [confirming, setConfirming] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  /** What the last refusal became — held, blocked, or confirm to publish (Story 25.4). */
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const workspaceId = workspace?.id ?? null
  const funnelId = route?.funnelId ?? null

  /* ── load ─────────────────────────────────────────────────────────────── */

  useEffect(() => {
    // `/funnels/new` has no funnel to load until a proposal is accepted (Story 27.4).
    if (!workspaceId || !funnelId || funnelId === PROPOSING) return
    // OPENED IN PLACE by accepting one: the store already holds the funnel, with each accepted
    // part on its undo stack, and loading it again would reset that history to nothing.
    if (editorState().funnelId === funnelId) return () => reset()
    let live = true
    void (async () => {
      try {
        const draft = await apiGet<Draft>(`/v1/w/${workspaceId}/funnels/${funnelId}`)
        if (!live) return
        load(draft.id, draft.schema)
        setMeta({ status: draft.status, archetype: draft.archetype })
      } catch (error) {
        if (!live) return
        setLoadError(
          error instanceof ApiFailure
            ? error.message
            : "We couldn't open that funnel. Check your connection and try again.",
        )
      }
    })()
    return () => {
      live = false
      // Nothing about one funnel outlives the tab that showed it — a stale
      // draft surviving into the next funnel would be saved onto it.
      reset()
    }
  }, [workspaceId, funnelId])

  /* ── save ─────────────────────────────────────────────────────────────── */

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const persist = useCallback(async () => {
    const state = editorState()
    if (!workspaceId || !state.funnelId || !state.funnel) return
    setSaveState({ status: 'saving' })
    // Captured BEFORE the request: an edit made while it is in flight must
    // leave the draft dirty rather than be marked clean by a save that did not
    // include it.
    const inFlight = state.funnel
    try {
      await apiPut(`/v1/w/${workspaceId}/funnels/${state.funnelId}`, inFlight)
      setSaveState(editorState().funnel === inFlight ? { status: 'clean' } : { status: 'dirty' })
    } catch (error) {
      setSaveState({
        status: 'failed',
        message:
          error instanceof ApiFailure
            ? error.message
            : "That change isn't saved yet. It's still here — check your connection.",
      })
    }
  }, [workspaceId])

  useEffect(() => {
    if (save.status !== 'dirty') return
    timer.current = setTimeout(() => void persist(), AUTOSAVE_MS)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [save.status, persist])

  /**
   * The last line of defence: a tab closed with an unsaved edit.
   *
   * The browser's own prompt, because it is the only thing that can stop a
   * close. Never shown when everything is saved.
   */
  useEffect(() => {
    if (save.status === 'clean' || save.status === 'saving') return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [save.status])

  /* ── publish ──────────────────────────────────────────────────────────── */

  /**
   * Story 16.1, and the seam Stories 6.5 and 6.6 left between them.
   *
   * IT FLUSHES THE DRAFT FIRST, and that is the part with a defect underneath
   * it rather than a preference. Publishing snapshots what the SERVER holds —
   * `publishFunnel` reads the row and writes `funnel_versions` — while the
   * editor autosaves 1200ms after the last keystroke. Pressing Publish inside
   * that window would otherwise snapshot the PREVIOUS save and say "Published",
   * and nothing on the screen would disagree. So a draft that is not clean is
   * persisted first, and a persist that fails abandons the publish: `save`
   * already renders its own reason, and publishing over it would bury the one
   * message that explains why the live version is not what is on screen.
   *
   * `attest` is the confirmation Story 25.4's dialog collects on Pro and above —
   * the verticals the refusal listed, and only those.
   */
  const publish = async (attest?: string[]) => {
    if (!workspaceId || !funnelId) return
    setPublishError(null)
    setPublishing(true)
    try {
      if (editorState().save.status !== 'clean') {
        await persist()
        if (editorState().save.status !== 'clean') {
          // The Callout above the tab body is already showing why.
          setConfirming(false)
          return
        }
      }
      await apiPost(`/v1/w/${workspaceId}/funnels/${funnelId}/publish`, attest ? { attest } : {})
      setMeta((prev) => (prev ? { ...prev, status: 'published' } : prev))
      setConfirming(false)
      setOutcome(null)
    } catch (error) {
      /**
       * A HELD, BLOCKED OR ATTESTATION REFUSAL IS AN OUTCOME, and the dialog
       * becomes it (Story 25.4) — decided by the code, in `publish-outcome.ts`.
       *
       * Anything else, the modal STAYS OPEN holding the message, exactly as
       * `ArchiveModal` does — including the 403 the route can still answer with
       * even when the button was enabled, because a permission can be revoked in
       * another tab and `useMe` is a boot call rather than a subscription.
       */
      const next = error instanceof ApiFailure ? outcomeOf(error) : null
      if (next) {
        setOutcome(next)
        return
      }
      setPublishError(
        error instanceof ApiFailure
          ? error.message
          : "We couldn't publish that funnel. Check your connection and try again.",
      )
    } finally {
      setPublishing(false)
    }
  }

  /* ── undo / redo ──────────────────────────────────────────────────────── */

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return
      const key = event.key.toLowerCase()
      if (key === 'z') {
        event.preventDefault()
        if (event.shiftKey) redo()
        else undo()
      } else if (key === 'y') {
        // The Windows habit. Harmless everywhere else.
        event.preventDefault()
        redo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  /* ── render ───────────────────────────────────────────────────────────── */

  const chooseTab = (next: TabId) => {
    setTab(next)
    if (!funnelId) return
    // `replaceState`, not `pushState`: the tab is not a navigation, and back
    // should leave the editor rather than walk six tabs backwards. The URL is
    // rewritten so a tab can still be linked to.
    window.history.replaceState(null, '', `/funnels/${funnelId}/${next}`)
  }

  if (!route) {
    return (
      <Frame>
        <Callout>That link doesn't name a funnel. Go back to Funnels and open one.</Callout>
      </Frame>
    )
  }

  if (route.funnelId === PROPOSING) {
    /*
      Story 27.4 — a proposal on an empty canvas, before any funnel exists. The chrome is the
      editor's own in its `pending` mode (`editor-build`, states `drafting` and `proposal`): its
      controls have nothing to act on yet, so none of their handlers can be reached.
    */
    const inert = () => undefined
    return (
      <div className="flex min-h-screen flex-col bg-mdx-ground">
        <EditorChrome
          pending
          name={PROPOSAL.untitled}
          status="draft"
          save="clean"
          tab="build"
          canPublish={false}
          publishing={false}
          onName={inert}
          onTab={inert}
          onPreview={inert}
          onPublish={inert}
        />
        <main className="flex min-w-0 flex-1 flex-col">
          {workspaceId ? (
            <ProposalView
              workspaceId={workspaceId}
              onAccepted={(draft) => {
                // The funnel exists now. The address says so — replaced, so Back leaves the editor
                // rather than returning to a proposal already accepted — and the editor opens on it
                // in place, the accepted parts on its undo stack.
                window.history.replaceState(null, '', `/funnels/${draft.id}`)
                setTab('build')
                setMeta({ status: draft.status, archetype: draft.archetype })
              }}
            />
          ) : null}
        </main>
      </div>
    )
  }

  if (loadError) {
    return (
      <Frame>
        <Callout>{loadError}</Callout>
        <span className="flex justify-center">
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </span>
      </Frame>
    )
  }

  if (!funnel || !meta) {
    return (
      <Frame>
        <Skeleton className="h-mdx-28" style={{ width: '38%' }} />
        <Skeleton className="h-mdx-18" style={{ width: '64%' }} />
        <Skeleton className="h-mdx-18" style={{ width: '48%' }} />
      </Frame>
    )
  }

  const active = TABS.find((t) => t.id === tab) ?? TABS[0]!

  return (
    <div className="flex min-h-screen flex-col bg-mdx-ground">
      <EditorChrome
        name={funnel.meta.name}
        status={meta.status}
        save={save.status}
        tab={tab}
        onName={(next) =>
          // Coalesced: a name is typed, and every keystroke would otherwise be
          // its own undo step.
          edit(
            'Rename the funnel',
            (draft) => {
              draft.meta.name = next
            },
            { coalesce: true },
          )
        }
        onTab={chooseTab}
        canPublish={workspace?.canPublish ?? false}
        publishing={publishing}
        /*
          The Design tab IS the preview (Story 6.5) — `Preview.tsx` renders
          there with the width controls `editor-design.html` draws beside it.
          There is no preview route in the API and this button does not invent
          one.
        */
        onPreview={() => chooseTab('design')}
        onPublish={() => {
          setPublishError(null)
          setOutcome(null)
          setConfirming(true)
        }}
      />

      <main
        className={
          tab === 'build' || tab === 'logic' || tab === 'design' || tab === 'connect'
            ? // Story 6.3's three columns run edge to edge, and Story 6.4's
              // centred column sets its own gutter — both as drawn.
              'flex min-w-0 flex-1 flex-col'
            : 'flex min-w-0 flex-1 flex-col gap-mdx-20 px-mdx-24 pb-mdx-48 pt-mdx-32'
        }
      >
        {save.status === 'failed' ? (
          // Its own gutter, because the Build tab's columns set theirs and this
          // must not sit flush against the window on that one tab.
          <div className={['build', 'logic', 'design', 'connect'].includes(tab) ? 'px-mdx-24 pt-mdx-20' : ''}>
            <Callout>
              {save.message} <SaveRetry onRetry={() => void persist()} />
            </Callout>
          </div>
        ) : null}

        {tab === 'build' ? (
          <BuildTab
            onOpenQualify={() => {
              chooseTab('qualify')
              // Below `lg` the inspector is a sheet at the foot of the page, so
              // the link is pressed far down it. The tab opens at its own top.
              window.scrollTo(0, 0)
            }}
          />
        ) : tab === 'logic' ? (
          <LogicTab />
        ) : tab === 'qualify' ? (
          <QualifyTab funnelId={route.funnelId} />
        ) : tab === 'design' ? (
          <DesignTab />
        ) : tab === 'connect' ? (
          // Story 6.7's Embed card. The Routing cards below it are Story 9.1.
          // `status` is passed so the Embed card re-reads when this funnel is
          // published from the chrome — its "not published yet" line is the one
          // place the person is told the snippet serves nothing yet, and it
          // must stop saying so in the same session.
          <ConnectTab funnelId={route.funnelId} status={meta.status} />
        ) : tab === 'results' ? (
          // Story 8.7. Drop-off by step is drawn in the export and is M6.
          <ResultsTab funnelId={route.funnelId} />
        ) : (
          <TabBody label={active.label} story={active.story} what={active.what} />
        )}
      </main>

      {confirming ? (
        /*
          The consequence, before the press — PRD §4.5, "never break a live
          embed on a client's site", is why the confirmation exists at all. It
          does not borrow `ArchiveModal`'s "no way back", and it promises no
          take-down (Q-087). Story 25.4 made it the one dialog a refused publish
          turns into as well: `PublishDialog`.
        */
        <PublishDialog
          name={funnel.meta.name}
          status={meta.status}
          org={org?.name ?? ''}
          person={me.user.name || me.user.email}
          busy={publishing}
          error={publishError}
          outcome={outcome}
          onClose={() => {
            setConfirming(false)
            setOutcome(null)
          }}
          onConfirm={(attest) => void publish(attest)}
        />
      ) : null}
    </div>
  )
}

/** A retry the person can press, inside the sentence that explains the problem. */
function SaveRetry({ onRetry }: { onRetry: () => void }) {
  return (
    <button
      type="button"
      onClick={onRetry}
      className="cursor-pointer border-0 bg-transparent p-0 font-mdx-semibold text-mdx-brand-deep underline"
    >
      Try saving again
    </button>
  )
}

/**
 * What a tab shows until its story fills it.
 *
 * The same honest placeholder `routes.tsx` uses for an unbuilt page, named for
 * the story rather than "coming soon" — a person who reads it knows whether to
 * wait or to ask.
 */
function TabBody({ label, story, what }: { label: string; story: string; what: string }) {
  return (
    <section className="flex flex-col items-center gap-mdx-12 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{label}</span>
      <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">{what}</span>
      <span className="text-mdx-13 text-mdx-slate">Story {story}</span>
    </section>
  )
}

/** The chrome-less frame the three pre-draft states sit in. */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-mdx-ground">
      <div className="flex min-h-mdx-topbar flex-none items-center border-b border-mdx-line bg-mdx-white px-mdx-16 py-mdx-11">
        <a
          href="/funnels"
          className="whitespace-nowrap text-mdx-13 font-mdx-semibold text-mdx-brand-deep no-underline"
        >
          ← Funnels
        </a>
      </div>
      <main className="mx-auto flex w-full max-w-mdx-measure-60 flex-col gap-mdx-16 px-mdx-24 pt-mdx-32">
        {children}
      </main>
    </div>
  )
}
